import React, { useState, useEffect } from 'react';
import { Card, Button, Table, Space, Tag, Typography, Modal, message, Input, QRCode } from 'antd';
import { PrinterOutlined, QrcodeOutlined, SearchOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { thermalLabelsAPI } from '../services/api';
import ThermalLabel from '../components/ThermalLabel';
import DishLabel from '../components/DishLabel';
import ComboLabel from '../components/ComboLabel';
import { useIsMobile } from '../hooks/useIsMobile';

const { Title, Text } = Typography;

interface OrderData {
  order: {
    id: number;
    status: string;
    totalAmount: number;
    createdAt: string;
    deliveryDate: string;
  };
  customer: {
    companyName: string;
    contactName: string;
    contactPhone: string;
    address: string;
  };
  supplier: {
    companyName: string;
    contactPhone: string;
  };
  items: Array<{
    id: number;
    name: string;
    quantity: number;
    price: number;
    weight: number;
    calories?: number | null;
    isVegan: boolean;
    isLenten: boolean;
    isHalal: boolean;
  }>;
  combos?: Array<{
    id: number;
    comboId: number;
    title: string;
    quantity: number;
    unitPrice: number;
    priceTotal: number;
    selectionsSnapshot: any;
  }>;
  qrCode: string;
  totalWeight: number;
  totalCalories: number;
}

interface DishData {
  orderItem: {
    id: number;
    quantity: number;
    price: number;
    isScanned: boolean;
    scannedAt?: string;
  };
  menuItem: {
    name: string;
    weight: number;
    calories?: number | null;
    isVegan: boolean;
    isLenten: boolean;
    isHalal: boolean;
  };
  order: {
    id: number;
    customerCompany: string;
    supplierCompany: string;
    supplierPhone: string;
  };
  orderedBy: string;
  qrCode: string;
  totalWeight: number;
  totalCalories: number;
  specialAttributes: string[];
}

interface ComboData {
  comboItem: {
    id: number;
    quantity: number;
    unitPrice: number;
    priceTotal: number;
  };
  combo: {
    title: string;
  };
  order: {
    id: number;
    customerCompany: string;
    supplierCompany: string;
    supplierPhone: string;
  };
  selectionsSnapshot: any;
  qrCode: string;
}

const ThermalPrinting: React.FC = () => {
  const isMobile = useIsMobile();
  const [orderData, setOrderData] = useState<OrderData | null>(null);
  const [dishData, setDishData] = useState<DishData | null>(null);
  const [comboData, setComboData] = useState<ComboData | null>(null);
  const [orderId, setOrderId] = useState<string>('');
  const [dishId, setDishId] = useState<string>('');
  const [comboId, setComboId] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [orderModalVisible, setOrderModalVisible] = useState(false);
  const [dishModalVisible, setDishModalVisible] = useState(false);
  const [comboModalVisible, setComboModalVisible] = useState(false);
  const [scanStats, setScanStats] = useState<any>(null);

  const loadOrderData = async () => {
    if (!orderId) {
      message.error('Введите ID заказа');
      return;
    }

    setLoading(true);
    try {
      const data = await thermalLabelsAPI.getOrderLabels(parseInt(orderId));
      setOrderData(data);
      setOrderModalVisible(true);
    } catch (error: any) {
      message.error(error?.response?.data?.error || 'Ошибка загрузки данных заказа');
    } finally {
      setLoading(false);
    }
  };

  const loadDishData = async () => {
    if (!dishId) {
      message.error('Введите ID блюда');
      return;
    }

    setLoading(true);
    try {
      const data = await thermalLabelsAPI.getDishLabels(parseInt(dishId));
      setDishData(data);
      setDishModalVisible(true);
    } catch (error: any) {
      message.error(error?.response?.data?.error || 'Ошибка загрузки данных блюда');
    } finally {
      setLoading(false);
    }
  };

  const loadComboData = async () => {
    if (!comboId) {
      message.error('Введите ID комбо-позиции');
      return;
    }

    setLoading(true);
    try {
      const data = await thermalLabelsAPI.getComboLabels(parseInt(comboId));
      setComboData(data);
      setComboModalVisible(true);
    } catch (error: any) {
      message.error(error?.response?.data?.error || 'Ошибка загрузки данных комбо');
    } finally {
      setLoading(false);
    }
  };

  const loadScanStats = async () => {
    if (!orderId) return;

    try {
      const stats = await thermalLabelsAPI.getScanStats(parseInt(orderId));
      setScanStats(stats);
    } catch (error) {
      console.error('Error loading scan stats:', error);
    }
  };

  const handlePrintOrder = () => {
    if (!orderData) return;
    
    // Создаем новое окно для печати
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <title>Печать заказа #${orderData.order.id}</title>
            <style>
              @media print {
                body { margin: 0; padding: 0; }
                .thermal-label { 
                  width: 80mm; 
                  height: 120mm; 
                  margin: 0; 
                  padding: 0;
                  page-break-after: always;
                }
              }
            </style>
          </head>
          <body>
            <div class="thermal-label">
              ${document.querySelector('.thermal-label')?.innerHTML || ''}
            </div>
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
    }
  };

  const handlePrintDish = () => {
    if (!dishData) return;
    
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <title>Печать блюда</title>
            <style>
              @media print {
                body { margin: 0; padding: 0; }
                .dish-label { 
                  width: 60mm; 
                  height: 80mm; 
                  margin: 0; 
                  padding: 0;
                  page-break-after: always;
                }
              }
            </style>
          </head>
          <body>
            <div class="dish-label">
              ${document.querySelector('.dish-label')?.innerHTML || ''}
            </div>
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
    }
  };

  const handlePrintCombo = () => {
    if (!comboData) return;

    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <title>Печать комбо</title>
            <style>
              @media print {
                body { margin: 0; padding: 0; }
                .combo-label { 
                  width: 60mm; 
                  height: 90mm; 
                  margin: 0; 
                  padding: 0;
                  page-break-after: always;
                }
              }
            </style>
          </head>
          <body>
            <div class="combo-label">
              ${document.querySelector('.combo-label')?.innerHTML || ''}
            </div>
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
    }
  };

  const handleScanDish = async (dishId: number) => {
    try {
      await thermalLabelsAPI.scanDish(dishId);
      message.success('Блюдо успешно отсканировано');
      loadScanStats();
    } catch (error: any) {
      message.error(error?.response?.data?.error || 'Ошибка сканирования');
    }
  };

  const handleScanCombo = async (orderComboItemId: number) => {
    try {
      await thermalLabelsAPI.scanCombo(orderComboItemId);
      message.success('Комбо успешно отсканировано');
      loadScanStats();
    } catch (error: any) {
      message.error(error?.response?.data?.error || 'Ошибка сканирования');
    }
  };

  useEffect(() => {
    if (orderId) {
      loadScanStats();
    }
  }, [orderId]);

  const scanStatsColumns = [
    {
      title: 'Позиция',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Статус',
      dataIndex: 'isScanned',
      key: 'isScanned',
      render: (isScanned: boolean, record: any) => (
        <Tag color={isScanned ? 'green' : 'orange'}>
          {isScanned ? '✅ Отсканировано' : '⏳ Ожидает'}
        </Tag>
      ),
    },
    {
      title: 'Время сканирования',
      dataIndex: 'scannedAt',
      key: 'scannedAt',
      render: (scannedAt: string) => 
        scannedAt ? new Date(scannedAt).toLocaleString('ru-RU') : '-',
    },
    {
      title: 'Действия',
      key: 'actions',
      render: (record: any) => (
        <Button 
          size="small" 
          disabled={record.isScanned}
          onClick={() => (record.type === 'combo' ? handleScanCombo(record.id) : handleScanDish(record.id))}
        >
          {record.isScanned ? 'Отсканировано' : 'Сканировать'}
        </Button>
      ),
    },
  ];

  return (
    <div style={{ padding: isMobile ? 12 : 24 }}>
      <Title level={2}>
        <PrinterOutlined style={{ marginRight: '8px' }} />
        Термопечать наклеек
      </Title>

      <Space direction="vertical" size={isMobile ? 'middle' : 'large'} style={{ width: '100%' }}>
        {/* Печать заказа */}
        <Card title="Печать наклейки заказа">
          <Space direction={isMobile ? 'vertical' : 'horizontal'} style={{ width: '100%' }}>
            <Input
              placeholder="ID заказа"
              value={orderId}
              onChange={(e) => setOrderId(e.target.value)}
              style={{ width: isMobile ? '100%' : 200 }}
              size={isMobile ? 'large' : 'middle'}
            />
            <Button 
              type="primary" 
              icon={<PrinterOutlined />}
              onClick={loadOrderData}
              loading={loading}
              block={isMobile}
            >
              Загрузить данные
            </Button>
          </Space>
        </Card>

        {/* Печать блюда */}
        <Card title="Печать наклейки блюда">
          <Space direction={isMobile ? 'vertical' : 'horizontal'} style={{ width: '100%' }}>
            <Input
              placeholder="ID блюда"
              value={dishId}
              onChange={(e) => setDishId(e.target.value)}
              style={{ width: isMobile ? '100%' : 200 }}
              size={isMobile ? 'large' : 'middle'}
            />
            <Button 
              type="primary" 
              icon={<QrcodeOutlined />}
              onClick={loadDishData}
              loading={loading}
              block={isMobile}
            >
              Загрузить данные
            </Button>
          </Space>
        </Card>

        {/* Печать комбо */}
        <Card title="Печать наклейки комбо">
          <Space direction={isMobile ? 'vertical' : 'horizontal'} style={{ width: '100%' }}>
            <Input
              placeholder="ID комбо-позиции"
              value={comboId}
              onChange={(e) => setComboId(e.target.value)}
              style={{ width: isMobile ? '100%' : 200 }}
              size={isMobile ? 'large' : 'middle'}
            />
            <Button type="primary" icon={<QrcodeOutlined />} onClick={loadComboData} loading={loading} block={isMobile}>
              Загрузить данные
            </Button>
          </Space>
        </Card>

        {/* Статистика сканирования */}
        {scanStats && (
          <Card title="Статистика сканирования">
            <Space direction="vertical" style={{ width: '100%' }}>
              <div>
                <Text strong>Прогресс сканирования: </Text>
                <Text>{scanStats.scannedItems} из {scanStats.totalItems} блюд</Text>
                <div style={{ 
                  width: '100%', 
                  backgroundColor: '#f0f0f0', 
                  borderRadius: '4px',
                  marginTop: '8px'
                }}>
                  <div style={{
                    width: `${scanStats.scanProgress}%`,
                    backgroundColor: '#52c41a',
                    height: '20px',
                    borderRadius: '4px',
                    transition: 'width 0.3s'
                  }} />
                </div>
              </div>
              
              <Table
                columns={scanStatsColumns}
                dataSource={scanStats.items}
                rowKey="id"
                pagination={false}
                size={isMobile ? 'small' : 'middle'}
                scroll={isMobile ? { x: 'max-content' } : undefined}
              />
            </Space>
          </Card>
        )}
      </Space>

      {/* Модальное окно для печати заказа */}
      <Modal
        title={`Печать заказа #${orderData?.order.id}`}
        open={orderModalVisible}
        onCancel={() => setOrderModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setOrderModalVisible(false)}>
            Отмена
          </Button>,
          <Button key="print" type="primary" onClick={handlePrintOrder}>
            Печать
          </Button>
        ]}
        width={isMobile ? '100%' : 800}
        centered={!isMobile}
        style={isMobile ? { top: 0, paddingBottom: 0 } : undefined}
      >
        {orderData && (
          <div style={{ textAlign: 'center' }}>
            <ThermalLabel data={orderData} />
          </div>
        )}
      </Modal>

      {/* Модальное окно для печати блюда */}
      <Modal
        title="Печать наклейки блюда"
        open={dishModalVisible}
        onCancel={() => setDishModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setDishModalVisible(false)}>
            Отмена
          </Button>,
          <Button key="print" type="primary" onClick={handlePrintDish}>
            Печать
          </Button>
        ]}
        width={isMobile ? '100%' : 600}
        centered={!isMobile}
        style={isMobile ? { top: 0, paddingBottom: 0 } : undefined}
      >
        {dishData && (
          <div style={{ textAlign: 'center' }}>
            <DishLabel data={dishData} />
          </div>
        )}
      </Modal>

      {/* Модальное окно для печати комбо */}
      <Modal
        title="Печать наклейки комбо"
        open={comboModalVisible}
        onCancel={() => setComboModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setComboModalVisible(false)}>
            Отмена
          </Button>,
          <Button key="print" type="primary" onClick={handlePrintCombo}>
            Печать
          </Button>
        ]}
        width={isMobile ? '100%' : 600}
        centered={!isMobile}
        style={isMobile ? { top: 0, paddingBottom: 0 } : undefined}
      >
        {comboData && (
          <div style={{ textAlign: 'center' }}>
            <div className="combo-label">
              <ComboLabel data={comboData} />
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ThermalPrinting;
